<template>
  <div class="page-main flex-page">
    <!--Header-->
    <div class="header-wrapper">
      <PageHeader
        :content="headerContent"
        :showButton="false"
        :showHeaderShadow="showOptions.headerShadow"
      ></PageHeader>
      <FormBuilder
        class="form-builder"
        :fields="fields"
        @fieldChanged="getAnalytics()"
      ></FormBuilder>
    </div>

    <div
      v-if="!fetchingAnalytics"
      ref="mainContent"
      class="main-content"
      @scroll="contentScroll"
    >
      <KeyMetrics :stats="analytics?.keyMetrics"></KeyMetrics>

      <div class="categories">
        <TableMetrics
          v-for="category in analytics?.categories"
          :key="category.title"
          :items="category.items"
          :title="category.title"
          :unit="category.unit"
          class="table"
        ></TableMetrics>
      </div>
    </div>

    <!--Pre Loader-->
    <PreLoader v-else :show="true" class="main-content"></PreLoader>
  </div>
</template>

<script>
// Libraries
import _ from "lodash";

// Components
import PageHeader from "@/components/dashboard/PageHeader";
import PreLoader from "@/components/loaders/PreLoader";
import FormBuilder from "@/components/form/FormBuilder";
import KeyMetrics from "@/components/dashboard/analytics/KeyMetrics";
import TableMetrics from "@/components/dashboard/analytics/TableMetrics";

// Defaults
import { APP_NAMES } from "@/mixins/Variables";

// Services
import { TranslationsAppService } from "@/services";
const { CompanyService, AnalyticsService } = TranslationsAppService;

export default {
  name: "Analytics",
  data() {
    return {
      lodash: _,
      showOptions: {
        headerShadow: false,
        lineLoader: false,
      },
      fields: {
        date: {
          type: "dropdown",
          placeholder: "Select date",
          required: true,
          hasError: false,
          fields: [],
          value: "",
        },
      },
      analytics: {},
      fetchingAnalytics: true,
      headerContent: {
        title: "Analytics",
        description:
          "Insights into the tickets and languages being used with AI translations",
      },
    };
  },
  props: {},
  components: {
    PageHeader,
    PreLoader,
    FormBuilder,
    KeyMetrics,
    TableMetrics,
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    selectedApp() {
      return this.$store.getters.selectedApp;
    },
  },
  watch: {},
  async created() {
    await this.initApp();

    this.initDates();
    await this.getAnalytics();
  },
  methods: {
    // Initialise the app
    async initApp() {
      if (_.isEmpty(this.selectedApp)) {
        const data = { companyId: this.company._id };
        const response = await CompanyService.GetCompany(data);

        this.$store.commit("updateSelectedApp", {
          appName: APP_NAMES.translations,
          ...response.data,
        });
      }
    },

    toDate(input) {
      if (input instanceof Date) return input;
      if (input && typeof input === "object" && "$date" in input)
        return new Date(input.$date);
      return new Date(input); // handles ISO string or epoch ms
    },

    initDates() {
      const start = this.toDate(this.selectedApp?.createdAt);
      const end = new Date();

      // Normalize both to the 1st of their months in UTC to avoid TZ/DST issues
      let d = new Date(
        Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1)
      );
      const endMonthUTC = new Date(
        Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), 1)
      );

      const fmt = new Intl.DateTimeFormat("en-US", {
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      });

      const options = [];
      while (d <= endMonthUTC) {
        options.push(fmt.format(d));
        d = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1));
      }

      this.fields.date.fields = options;
      this.fields.date.value = options[options.length - 1];
    },

    // Get insights
    async getAnalytics() {
      this.fetchingAnalytics = true;

      const payload = this.constructPayload();
      const response = await AnalyticsService.GetAnalytics(payload);
      if (!response.hasError) this.analytics = response.data;

      this.fetchingAnalytics = false;
    },

    constructPayload() {
      const data = {
        companyId: this.company._id,
        ...this.parseData(this.fields),
      };
      return data;
    },

    // Event on scroll
    contentScroll() {
      const scrollTop = this.$refs.mainContent.scrollTop;

      // Check if header needs shadow
      if (scrollTop > 0) this.showOptions.headerShadow = true;
      else this.showOptions.headerShadow = false;
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/shared/dashboard.scss";

.header-wrapper {
  position: relative;
  z-index: 1;

  .form-builder {
    z-index: 1;
    position: absolute;
    top: 50%;
    right: 0;
    transform: translateY(calc(-50% + 1rem));

    ::v-deep {
      .dropdown-info-wrapper {
        margin-top: -0.5rem;
        background-color: $blackColor;

        .field-placeholder {
          font-size: $smallFontSize;
          color: darken($whiteColor, 10%) !important;
          width: max-content;
          margin-right: 1.5rem;
        }

        .unicon svg {
          fill: darken($whiteColor, 30%);
        }
      }
    }
  }
}

.categories {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: center;
  gap: 2rem;
  margin-top: 2rem;

  .table {
    flex: 1;
  }
}
</style>
